/**
 * Road Network Generation
 * Connects entry points, districts, and buildings with organic roads.
 */

import type { Road, District, Building, EntryPoint, Point, SettlementConfig } from './types';
import { SeededRandom } from './random';
import { distance, lerp, smoothPolyline, fromPolar, angleBetween } from './geometry';
import { TIER_TEMPLATES } from './templates';

// ============================================================================
// ENTRY POINT GENERATION
// ============================================================================

export function generateEntryPoints(
  config: SettlementConfig,
  districts: District[],
  rng: SeededRandom,
): EntryPoint[] {
  const tier = TIER_TEMPLATES[config.tier];
  const count = config.entryRoadCount ?? rng.int(tier.entryRoads[0], tier.entryRoads[1]);

  // Distribute entry points evenly around the settlement
  const baseAngle = rng.angle();
  const angleStep = (Math.PI * 2) / count;

  // Find max district extent for placing entries beyond it
  let maxExtent = 0;
  for (const d of districts) {
    for (const p of d.boundary) {
      const dist = Math.sqrt(p.x * p.x + p.y * p.y);
      if (dist > maxExtent) maxExtent = dist;
    }
  }
  const entryRadius = maxExtent + 30;

  const entries: EntryPoint[] = [];
  const labels = ['North Road', 'East Road', 'South Road', 'West Road', 'River Road', 'Forest Path'];

  for (let i = 0; i < count; i++) {
    const angle = baseAngle + i * angleStep + rng.range(-0.2, 0.2);
    const pos = fromPolar(angle, entryRadius);
    entries.push({
      id: `entry_${i}`,
      position: pos,
      direction: angle,
      label: labels[i % labels.length],
    });
  }

  return entries;
}

// ============================================================================
// ROAD NETWORK
// ============================================================================

export function generateRoads(
  _config: SettlementConfig,
  districts: District[],
  buildings: Building[],
  entryPoints: EntryPoint[],
  rng: SeededRandom,
): Road[] {
  const roads: Road[] = [];
  let roadIdx = 0;
  const center = districts.find(d => d.type === 'center')?.center ?? { x: 0, y: 0 };

  // Phase 1: Main roads — entry points to center
  for (const entry of entryPoints) {
    const waypoints: Point[] = [entry.position];

    // Find the closest district center to pass through
    const nearestDistrict = findNearest(entry.position, districts.filter(d => d.type !== 'center'));
    if (nearestDistrict) {
      // Add waypoint near that district (offset slightly for organic feel)
      waypoints.push({
        x: nearestDistrict.center.x + rng.range(-5, 5),
        y: nearestDistrict.center.y + rng.range(-5, 5),
      });
    }

    waypoints.push({ x: center.x + rng.range(-3, 3), y: center.y + rng.range(-3, 3) });

    roads.push({
      id: `road_${roadIdx++}`,
      tier: 'main',
      points: smoothPolyline(waypoints, 2),
      fromId: entry.id,
      toId: districts.find(d => d.type === 'center')?.id ?? null,
    });
  }

  // Phase 2: District roads — connect adjacent district centers
  const connected = new Set<string>();
  const sortedDistricts = [...districts].sort((a, b) =>
    distance(a.center, center) - distance(b.center, center)
  );

  for (let i = 0; i < sortedDistricts.length; i++) {
    for (let j = i + 1; j < sortedDistricts.length; j++) {
      const a = sortedDistricts[i];
      const b = sortedDistricts[j];
      const key = [a.id, b.id].sort().join('|');
      if (connected.has(key)) continue;

      const dist = distance(a.center, b.center);
      // Connect if reasonably close
      if (dist < 120) {
        connected.add(key);
        const mid = lerp(a.center, b.center, 0.5);
        // Add slight perpendicular offset for organic curve
        const perpOffset = rng.range(-8, 8);
        const angle = angleBetween(a.center, b.center) + Math.PI / 2;
        const waypoint: Point = {
          x: mid.x + Math.cos(angle) * perpOffset,
          y: mid.y + Math.sin(angle) * perpOffset,
        };

        roads.push({
          id: `road_${roadIdx++}`,
          tier: 'secondary',
          points: smoothPolyline([a.center, waypoint, b.center], 1),
          fromId: a.id,
          toId: b.id,
        });
      }
    }
  }

  // Phase 3: Alleys — short paths between clustered buildings
  const alleyPairs = new Set<string>();
  for (const building of buildings) {
    // Find nearest other building in same district
    const sameDistrict = buildings.filter(
      b => b.id !== building.id && b.districtId === building.districtId
    );
    if (sameDistrict.length === 0) continue;

    const nearest = findNearestBuilding(building.footprint.center, sameDistrict);
    if (!nearest) continue;

    const key = [building.id, nearest.id].sort().join('|');
    if (alleyPairs.has(key)) continue;

    const dist = distance(building.footprint.center, nearest.footprint.center);
    if (dist < 20 && rng.bool(0.6)) {
      alleyPairs.add(key);
      roads.push({
        id: `road_${roadIdx++}`,
        tier: 'alley',
        points: [building.footprint.center, nearest.footprint.center],
        fromId: building.id,
        toId: nearest.id,
      });
    }
  }

  return roads;
}

// ============================================================================
// HELPERS
// ============================================================================

function findNearest(pos: Point, districts: District[]): District | null {
  let best: District | null = null;
  let bestDist = Infinity;
  for (const d of districts) {
    const dist = distance(pos, d.center);
    if (dist < bestDist) {
      bestDist = dist;
      best = d;
    }
  }
  return best;
}

function findNearestBuilding(pos: Point, buildings: Building[]): Building | null {
  let best: Building | null = null;
  let bestDist = Infinity;
  for (const b of buildings) {
    const dist = distance(pos, b.footprint.center);
    if (dist < bestDist) {
      bestDist = dist;
      best = b;
    }
  }
  return best;
}
